import { createRoot } from 'react-dom/client';
import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, Info, CheckCircle2, XCircle } from 'lucide-react';

type Variant = 'primary' | 'danger' | 'warning' | 'success';

interface ConfirmOptions {
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: Variant;
}

const variantConfig: Record<Variant, { btn: string; icon: typeof AlertTriangle; iconColor: string; ring: string }> = {
  primary: { btn: 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500', icon: Info, iconColor: 'text-blue-600', ring: 'focus:ring-blue-500' },
  danger: { btn: 'bg-red-600 hover:bg-red-700 focus:ring-red-500', icon: AlertTriangle, iconColor: 'text-red-600', ring: 'focus:ring-red-500' },
  warning: { btn: 'bg-amber-600 hover:bg-amber-700 focus:ring-amber-500', icon: AlertTriangle, iconColor: 'text-amber-600', ring: 'focus:ring-amber-500' },
  success: { btn: 'bg-green-600 hover:bg-green-700 focus:ring-green-500', icon: CheckCircle2, iconColor: 'text-green-600', ring: 'focus:ring-green-500' },
};

function ConfirmDialogShell({ options, onResult }: { options: ConfirmOptions; onResult: (value: boolean) => void }) {
  const [visible, setVisible] = useState(false);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  const cfg = variantConfig[options.variant ?? 'primary'];
  const Icon = cfg.icon;

  useEffect(() => {
    // Fade-in nach mount
    requestAnimationFrame(() => setVisible(true));
    confirmRef.current?.focus();

    // Trap focus + Escape-Handler
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close(false);
      } else if (e.key === 'Tab') {
        const focusables = dialogRef.current?.querySelectorAll<HTMLElement>('button, [href], [tabindex]:not([tabindex="-1"])');
        if (!focusables || focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          last.focus();
          e.preventDefault();
        } else if (!e.shiftKey && document.activeElement === last) {
          first.focus();
          e.preventDefault();
        }
      }
    };
    window.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, []);

  const close = (value: boolean) => {
    setVisible(false);
    setTimeout(() => onResult(value), 160);
  };

  return (
    <div
      className={`fixed inset-0 z-[10000] flex items-center justify-center px-4 transition-opacity duration-150 ${visible ? 'opacity-100' : 'opacity-0'}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="bp-confirm-title"
      aria-describedby="bp-confirm-message"
    >
      <div className="absolute inset-0 bg-black/40 dark:bg-black/60" onClick={() => close(false)} />
      <div
        ref={dialogRef}
        className={`relative w-full max-w-md bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-6 transform transition-all duration-150 ${visible ? 'scale-100' : 'scale-95'}`}
      >
        <div className="flex items-start gap-4">
          <div className={`shrink-0 ${cfg.iconColor}`}>
            <Icon size={28} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 id="bp-confirm-title" className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              {options.title ?? 'Bitte bestätigen'}
            </h3>
            <p id="bp-confirm-message" className="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">
              {options.message}
            </p>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={() => close(false)}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 dark:focus:ring-offset-gray-800 transition-colors min-h-[40px]"
          >
            {options.cancelLabel ?? 'Abbrechen'}
          </button>
          <button
            ref={confirmRef}
            type="button"
            onClick={() => close(true)}
            className={`px-4 py-2 text-sm font-medium text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-800 transition-colors min-h-[40px] ${cfg.btn}`}
          >
            {options.confirmLabel ?? 'Bestätigen'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Promise-basierter Modal-Confirm — Ersatz für window.confirm().
 * Stylebar, accessible (role=dialog, focus-trap, ESC), Screen-Reader-tauglich.
 *
 * @example
 *   if (!(await confirmDialog({ message: 'Wirklich löschen?', variant: 'danger' }))) return;
 */
export function confirmDialog(options: ConfirmOptions | string): Promise<boolean> {
  const opts: ConfirmOptions = typeof options === 'string' ? { message: options } : options;

  return new Promise((resolve) => {
    const container = document.createElement('div');
    document.body.appendChild(container);
    const root = createRoot(container);

    const handleResult = (value: boolean) => {
      resolve(value);
      // Unmount nach Animation
      setTimeout(() => {
        root.unmount();
        if (container.parentNode) container.parentNode.removeChild(container);
      }, 50);
    };

    root.render(<ConfirmDialogShell options={opts} onResult={handleResult} />);
  });
}

export default confirmDialog;
